/** Pure rules + Thai text of the Health page (สุขภาพระบบ) and the "บริการล่ม" banner. No Vue, no I/O. */
import type { StaffRole } from '../api/session'
import type {
  HealthCheck,
  HealthCounts,
  HealthData,
  HealthIncident,
  HealthPoint,
  HealthStatus,
  HealthSummary,
  HealthSummaryCounts,
} from '../api/health/types'
import { clockTime, dayKey, shortDate } from './inbox'

export const DAY_MS = 86_400_000

/** Owner + head only (the API answers 403 to staff). */
export function canSeeHealth(role: StaffRole | null | undefined): boolean {
  return role === 'owner' || role === 'head'
}

export const STATUS_LABEL: Record<HealthStatus, string> = {
  ok: 'ปกติ',
  warn: 'เฝ้าระวัง',
  fail: 'ผิดปกติ',
  unknown: 'ตรวจไม่ได้',
}
export const DOWN_LABEL = 'ล่ม'

/** Row look: a failing check that is `down` (failed twice in a row) reads "ล่ม". */
export type RowState = HealthStatus | 'down'

export function rowState(c: Pick<HealthCheck, 'status' | 'down'>): RowState {
  return c.down ? 'down' : c.status
}

export function rowLabel(s: RowState): string {
  return s === 'down' ? DOWN_LABEL : STATUS_LABEL[s]
}

/** fail > warn > unknown > ok (same order as the API's `worst`). */
const RANK: Record<HealthStatus, number> = { fail: 3, warn: 2, unknown: 1, ok: 0 }

export function statusRank(s: HealthStatus): number {
  return RANK[s]
}

export function worstOf(statuses: Iterable<HealthStatus>): HealthStatus {
  let worst: HealthStatus = 'ok'
  for (const s of statuses) if (RANK[s] > RANK[worst]) worst = s
  return worst
}

const byName = new Intl.Collator('th')

/** Down first, then worst status, then name. */
export function sortChecks<T extends Pick<HealthCheck, 'status' | 'down' | 'name'>>(checks: readonly T[]): T[] {
  return [...checks].sort(
    (a, b) => Number(b.down) - Number(a.down) || RANK[b.status] - RANK[a.status] || byName.compare(a.name, b.name),
  )
}

export function countStatuses(checks: readonly Pick<HealthCheck, 'status'>[]): HealthCounts {
  const out: HealthCounts = { ok: 0, warn: 0, fail: 0, unknown: 0 }
  for (const c of checks) out[c.status] += 1
  return out
}

export interface CountPart {
  key: RowState
  label: string
  n: number
}

/**
 * "ปกติ 30 · เฝ้าระวัง 2 · ผิดปกติ 1 · ล่ม 2 · ตรวจไม่ได้ 3" — ผิดปกติ counts only failures that are not (yet) down;
 * zero counts are left out except ปกติ.
 */
export function countParts(s: Pick<HealthSummaryCounts, 'ok' | 'warn' | 'fail' | 'unknown'> & { down?: number }): CountPart[] {
  const down = s.down ?? 0
  const parts: CountPart[] = [
    { key: 'ok', label: STATUS_LABEL.ok, n: s.ok },
    { key: 'warn', label: STATUS_LABEL.warn, n: s.warn },
    { key: 'fail', label: STATUS_LABEL.fail, n: Math.max(0, s.fail - down) },
    { key: 'down', label: DOWN_LABEL, n: down },
    { key: 'unknown', label: STATUS_LABEL.unknown, n: s.unknown },
  ]
  return parts.filter((p) => p.key === 'ok' || p.n > 0)
}

export function countsText(s: Parameters<typeof countParts>[0]): string {
  return countParts(s)
    .map((p) => `${p.label} ${p.n}`)
    .join(' · ')
}

/** "6 นาที" · "2 ชม. 10 นาที" · "3 วัน 4 ชม." (floors; under a minute → "ไม่ถึง 1 นาที"). */
export function durationText(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60000)
  if (min < 1) return 'ไม่ถึง 1 นาที'
  if (min < 60) return `${min} นาที`
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h < 24) return m ? `${h} ชม. ${m} นาที` : `${h} ชม.`
  const d = Math.floor(h / 24)
  const hh = h % 24
  return hh ? `${d} วัน ${hh} ชม.` : `${d} วัน`
}

/** "เมื่อสักครู่" · "5 นาทีที่แล้ว" · "3 ชม.ที่แล้ว" · older: "25 ก.ย. 21:10". */
export function agoText(iso: string | null, nowMs: number): string {
  if (!iso) return 'ยังไม่เคยตรวจ'
  const t = Date.parse(iso)
  const sec = Math.floor((nowMs - t) / 1000)
  if (sec < 45) return 'เมื่อสักครู่'
  if (sec < 3600) return `${Math.max(1, Math.round(sec / 60))} นาทีที่แล้ว`
  if (sec < DAY_MS / 1000) return `${Math.floor(sec / 3600)} ชม.ที่แล้ว`
  return `${shortDate(iso)} ${clockTime(iso)}`
}

/** Row meta: "ตรวจเมื่อสักครู่" · "ตรวจเมื่อ 5 นาทีที่แล้ว" · "ยังไม่เคยตรวจ". */
export function checkedText(iso: string | null, nowMs: number): string {
  if (!iso) return 'ยังไม่เคยตรวจ'
  const a = agoText(iso, nowMs)
  return a === 'เมื่อสักครู่' ? 'ตรวจเมื่อสักครู่' : `ตรวจเมื่อ ${a}`
}

/** Clock time, with the date when it is not today (Bangkok): "21:04" · "25 ก.ย. 21:04". */
export function whenText(iso: string, nowMs: number): string {
  return dayKey(iso) === dayKey(nowMs) ? clockTime(iso) : `${shortDate(iso)} ${clockTime(iso)}`
}

/** "ทุก 30 วินาที" · "ทุก 2 นาที" · "ทุก 1 ชม." · "วันละครั้ง". */
export function intervalText(s: number): string {
  if (s <= 0) return '—'
  if (s < 60) return `ทุก ${s} วินาที`
  if (s < 3600) return `ทุก ${Math.round(s / 60)} นาที`
  if (s < 86400) return Number.isInteger(s / 3600) ? `ทุก ${s / 3600} ชม.` : `ทุก ${Math.round(s / 60)} นาที`
  if (s === 86400) return 'วันละครั้ง'
  return `ทุก ${Math.round(s / 86400)} วัน`
}

/** "412 ms" · "1.2 วินาที" · "" when unknown. */
export function latencyText(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return ''
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(1)} วินาที`
}

// ---------------------------------------------------------------- 24 h timeline

export type SegmentStatus = HealthStatus | 'none'

export interface Segment {
  from: number
  to: number
  status: SegmentStatus
  /** position in the bar, 0–100 */
  left: number
  width: number
}

/**
 * Timeline points → bar segments over [now - window, now]. Points before the window are clamped to its start
 * (the status still in effect then); time before the first point is 'none' (never checked). Consecutive equal
 * statuses merge; zero-length pieces drop.
 */
export function timelineSegments(points: readonly HealthPoint[], nowMs: number, windowMs = DAY_MS): Segment[] {
  const start = nowMs - windowMs
  const sorted = [...points]
    .map((p) => ({ t: Date.parse(p.at), status: p.status as SegmentStatus }))
    .filter((p) => Number.isFinite(p.t) && p.t <= nowMs)
    .sort((a, b) => a.t - b.t)
  // the status in effect at the window start = last point at or before it
  const pieces: { t: number; status: SegmentStatus }[] = []
  let before: SegmentStatus = 'none'
  for (const p of sorted) {
    if (p.t <= start) before = p.status
    else pieces.push(p)
  }
  const marks = [{ t: start, status: before }, ...pieces]
  const out: Segment[] = []
  for (let i = 0; i < marks.length; i++) {
    const from = marks[i]!.t
    const to = i + 1 < marks.length ? marks[i + 1]!.t : nowMs
    if (to <= from) continue
    const status = marks[i]!.status
    const last = out[out.length - 1]
    if (last && last.status === status) {
      last.to = to
    } else {
      out.push({ from, to, status, left: 0, width: 0 })
    }
  }
  for (const s of out) {
    s.left = ((s.from - start) / windowMs) * 100
    s.width = ((s.to - s.from) / windowMs) * 100
  }
  return out
}

const SEG_LABEL: Record<SegmentStatus, string> = { ...STATUS_LABEL, none: 'ยังไม่ได้ตรวจ' }

export function segmentLabel(s: SegmentStatus): string {
  return SEG_LABEL[s]
}

/** Screen-reader line: "24 ชม.ล่าสุด: ปกติ 21 ชม. 50 นาที · ผิดปกติ 2 ชม. 10 นาที" (longest first). */
export function timelineSummary(segments: readonly Segment[]): string {
  if (!segments.length) return '24 ชม.ล่าสุด: ยังไม่มีผลตรวจ'
  const total = new Map<SegmentStatus, number>()
  for (const s of segments) total.set(s.status, (total.get(s.status) ?? 0) + (s.to - s.from))
  const parts = [...total.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([st, ms]) => `${SEG_LABEL[st]} ${durationText(ms)}`)
  return `24 ชม.ล่าสุด: ${parts.join(' · ')}`
}

/** Tick labels every 6 h, left → right. */
export const TIMELINE_TICKS: { pct: number; label: string }[] = [
  { pct: 0, label: '24 ชม.ก่อน' },
  { pct: 25, label: '18 ชม.' },
  { pct: 50, label: '12 ชม.' },
  { pct: 75, label: '6 ชม.' },
  { pct: 100, label: 'ตอนนี้' },
]

// ---------------------------------------------------------------- incidents

export function incidentText(i: Pick<HealthIncident, 'kind' | 'to'>): string {
  if (i.kind === 'down') return 'ล่ม'
  if (i.kind === 'recovered') return 'กลับมาปกติ'
  return i.to === 'ok' ? 'กลับเป็นปกติ' : `เปลี่ยนเป็น${STATUS_LABEL[i.to]}`
}

export function incidentState(i: Pick<HealthIncident, 'kind' | 'to'>): RowState {
  if (i.kind === 'down') return 'down'
  if (i.kind === 'recovered') return 'ok'
  return i.to
}

// ---------------------------------------------------------------- banner

/** Identity of "what is down now": the banner stays hidden after "ซ่อน" until this changes. */
export function bannerKey(s: Pick<HealthSummary, 'down_items'> | null): string {
  if (!s) return ''
  return [...new Set(s.down_items.map((d) => d.key))].sort().join(',')
}

/** "เว็บสั่งซื้อ /order, ส่ง SMS แจ้งลูกค้า และอีก 1 รายการ" */
export function downNames(s: Pick<HealthSummary, 'down_items' | 'down'>, max = 2): string {
  const names = s.down_items.map((d) => d.name)
  const shown = names.slice(0, max).join(', ')
  const more = Math.max(s.down, names.length) - Math.min(max, names.length)
  return more > 0 ? `${shown} และอีก ${more} รายการ` : shown
}

/** The banner's summary from a full GET /health (so it updates right after a run). */
export function summaryFromData(d: HealthData): HealthSummary {
  const all = d.groups.flatMap((g) => g.checks)
  const down = all.filter((c) => c.down)
  return {
    down: down.length,
    down_items: down.map((c) => ({ key: c.key, name: c.name, since: c.down_since })),
    fail: all.filter((c) => c.status === 'fail').length,
    warn: all.filter((c) => c.status === 'warn').length,
    checked_at: d.checked_at,
  }
}

/** The headline of the page: the worst thing first. */
export function headline(s: HealthSummaryCounts): { state: RowState; text: string } {
  if (s.down > 0) return { state: 'down', text: `บริการล่ม ${s.down} รายการ` }
  if (s.fail > 0) return { state: 'fail', text: `ผิดปกติ ${s.fail} รายการ · รอยืนยันรอบถัดไป` }
  if (s.warn > 0) return { state: 'warn', text: `ต้องเฝ้าระวัง ${s.warn} รายการ` }
  if (s.total > 0 && s.unknown === s.total) return { state: 'unknown', text: 'ยังตรวจไม่ได้' }
  if (s.total === 0) return { state: 'unknown', text: 'ยังไม่มีผลตรวจ' }
  return { state: 'ok', text: 'ทุกบริการทำงานปกติ' }
}
